import { createTranslationHook } from '../i18nFactory';
import { TEXTO_COMUN_ES, TEXTO_COMUN_EN } from '../commonTexts';
import type { TranslateFn } from '../i18nFactory';

/**
 * i18n del SerialPanel (soporte nativo de seriales en MasterDocument).
 *
 * Sigue el mismo patrón que el resto del kit: usa `useTranslation`; si la clave
 * no está registrada en el host (el motor devuelve la propia clave), cae al
 * diccionario local del idioma activo. Claves en formato plano (no anidado).
 *
 * ## Claves con número y SIN variantes de plural (decisión, no olvido)
 *
 * `counter_label`, `select_counter_label` y `count_mismatch` llevan números pero
 * no declaran `_one` / `_other`, porque en ninguna el número gobierna la
 * concordancia del texto:
 *
 * - `counter_label` / `select_counter_label` son contadores de progreso
 *   (`Capturados: 1 / 5`). El participio concuerda con la colección, no con el
 *   valor actual, así que un `_one` produciría "Capturado: 1 / 5", que es peor.
 * - `count_mismatch` pone las dos cantidades entre paréntesis, fuera de la
 *   sintaxis de la frase.
 *
 * Los call sites las invocan sin `count`, así que el sondeo de plural del factory
 * ni se activa. Si se añade el gate que exige variantes a toda clave con
 * `{count}`, estas tres quedan fuera por construcción.
 */

export const SERIAL_PANEL_TRANSLATIONS_ES: Record<string, string> = {
  'serial_panel.alert_required':
    'Este ítem requiere captura de serial. Ingrese un serial por unidad de la cantidad.',
  'serial_panel.counter_label': 'Capturados: {captured} / {required}',
  'serial_panel.select_counter_label': 'Seleccionados: {selected} / {required}',
  'serial_panel.captured_section': 'Seriales capturados',
  'serial_panel.selected_section': 'Seriales seleccionados para salida',
  'serial_panel.add': TEXTO_COMUN_ES.add,
  'serial_panel.duplicate_error': 'Este serial ya fue capturado en esta línea.',
  'serial_panel.not_available_error': 'Serial no disponible en la bodega origen.',
  'serial_panel.overcount_error_one':
    'Ya seleccionó {count} serial — desmarque uno antes de seleccionar otro.',
  'serial_panel.overcount_error_other':
    'Ya seleccionó {count} seriales — desmarque uno antes de seleccionar otro.',
  'serial_panel.count_mismatch':
    'La cantidad de seriales ({captured}) no coincide con la cantidad del ítem ({required}).',
  'serial_panel.save_tooltip_one': 'Capture {count} serial antes de guardar.',
  'serial_panel.save_tooltip_other': 'Capture {count} seriales antes de guardar.',
  'serial_panel.search_placeholder': 'Buscar por código de serial...',
  'serial_panel.empty_state': 'No hay seriales disponibles para este ítem y bodega.',
  'serial_panel.empty_title': 'Sin seriales disponibles',
  'serial_panel.input_placeholder': 'Escanee o escriba el serial y presione Enter',
  'serial_panel.input_label': 'Serial',
  'serial_panel.validating': 'Validando serial',
  'serial_panel.chip_empty': 'Aún no se han capturado seriales.',
  'serial_panel.readonly_banner': 'Documento en estado no editable — modo solo lectura.',
  'serial_panel.col_serial': 'Serial',
  'serial_panel.col_lot': 'Lote',
  'serial_panel.col_location': 'Ubicación',
  'serial_panel.col_status': 'Estado',
  'serial_panel.status_created': 'Creado',
  'serial_panel.status_available': 'Disponible',
  'serial_panel.status_active': 'Activo',
  'serial_panel.status_committed': 'Comprometido',
  'serial_panel.status_unavailable': 'No disponible',
  'serial_panel.pagination': 'Mostrando {from}–{to} de {total}',
  'serial_panel.remove_aria': 'Quitar serial',
  'serial_panel.confirm_selection':
    'El serial {serialCode} tiene movimientos. ¿Desea continuar?',
  'serial_panel.unsupported':
    'El manejo de seriales está configurado para este ítem, pero el adaptador no implementa el método requerido. Contacte al administrador.',
  'serial_panel.section_title': 'Seriales',
  'serial_panel.max_reached_one':
    'Ya capturó {count} serial (la cantidad requerida). Quite uno antes de agregar otro.',
  'serial_panel.max_reached_other':
    'Ya capturó {count} seriales (la cantidad requerida). Quite uno antes de agregar otro.',
};

export const SERIAL_PANEL_TRANSLATIONS_EN: Record<string, string> = {
  'serial_panel.alert_required':
    'This item requires serial capture. Enter one serial per unit of the quantity.',
  'serial_panel.counter_label': 'Captured: {captured} / {required}',
  'serial_panel.select_counter_label': 'Selected: {selected} / {required}',
  'serial_panel.captured_section': 'Captured serials',
  'serial_panel.selected_section': 'Serials selected for issue',
  'serial_panel.add': TEXTO_COMUN_EN.add,
  'serial_panel.duplicate_error': 'This serial was already captured in this line.',
  'serial_panel.not_available_error': 'Serial not available in the source warehouse.',
  'serial_panel.overcount_error_one':
    'You already selected {count} serial — unselect one before selecting another.',
  'serial_panel.overcount_error_other':
    'You already selected {count} serials — unselect one before selecting another.',
  'serial_panel.count_mismatch':
    'The number of serials ({captured}) does not match the item quantity ({required}).',
  'serial_panel.save_tooltip_one': 'Capture {count} serial before saving.',
  'serial_panel.save_tooltip_other': 'Capture {count} serials before saving.',
  'serial_panel.search_placeholder': 'Search by serial code...',
  'serial_panel.empty_state': 'No serials available for this item and warehouse.',
  'serial_panel.empty_title': 'No serials available',
  'serial_panel.input_placeholder': 'Scan or type the serial and press Enter',
  'serial_panel.input_label': 'Serial',
  'serial_panel.validating': 'Validating serial',
  'serial_panel.chip_empty': 'No serials captured yet.',
  'serial_panel.readonly_banner': 'Document is not editable — read-only mode.',
  'serial_panel.col_serial': 'Serial',
  'serial_panel.col_lot': 'Lot',
  'serial_panel.col_location': 'Location',
  'serial_panel.col_status': 'Status',
  'serial_panel.status_created': 'Created',
  'serial_panel.status_available': 'Available',
  'serial_panel.status_active': 'Active',
  'serial_panel.status_committed': 'Committed',
  'serial_panel.status_unavailable': 'Unavailable',
  'serial_panel.pagination': 'Showing {from}–{to} of {total}',
  'serial_panel.remove_aria': 'Remove serial',
  'serial_panel.confirm_selection':
    'Serial {serialCode} has movements. Do you want to continue?',
  'serial_panel.unsupported':
    'Serial handling is configured for this item, but the adapter does not implement the required method. Contact your administrator.',
  'serial_panel.section_title': 'Serials',
  'serial_panel.max_reached_one':
    'You already captured {count} serial (the required quantity). Remove one before adding another.',
  'serial_panel.max_reached_other':
    'You already captured {count} serials (the required quantity). Remove one before adding another.',
};

/** Diccionarios de respaldo por idioma, listos para registrar en el host si se desea. */
export const SERIAL_PANEL_TRANSLATIONS = {
  es: SERIAL_PANEL_TRANSLATIONS_ES,
  en: SERIAL_PANEL_TRANSLATIONS_EN,
} as const;

/** Función de traducción con interpolación `{var}`. */
export type SerialPanelT = TranslateFn;

/**
 * Hook de traducción del SerialPanel. Usa `useTranslation` del kit; si la clave
 * no está registrada en el host, cae al diccionario local del idioma activo.
 */
export const useSerialPanelTranslation = createTranslationHook(SERIAL_PANEL_TRANSLATIONS);
